<script setup lang="ts">
/**
 * 分栏拖拽手柄：拖动改宽度，双击复位。
 *
 * 放在画布区（`.canvas-wrap`）内部贴边，所以不会被相邻面板的 overflow 裁掉；
 * 指针捕获只是"拖出 7px 条也能继续拖"的优化 —— 所以一律 try/catch（捕获失败不能中断拖动）。
 */
import { ref } from 'vue';
import { resetPanelWidth, saveLayout, setPanelWidth, ui } from '@/stores/editor';

const props = defineProps<{ side: 'list' | 'right' }>();

const dragging = ref(false);
let startX = 0;
let startWidth = 0;

const width = (): number => (props.side === 'list' ? ui.listW : ui.inspW);

const capture = (el: Element | null, id: number, on: boolean): void => {
  try {
    if (on) el?.setPointerCapture?.(id);
    else el?.releasePointerCapture?.(id);
  } catch {
    /* 指针已失效：忽略 */
  }
};

const onDown = (e: PointerEvent): void => {
  dragging.value = true;
  startX = e.clientX;
  startWidth = width();
  capture(e.currentTarget as Element | null, e.pointerId, true);
  document.body.classList.add('is-resizing');
  e.preventDefault();
};

const onMove = (e: PointerEvent): void => {
  if (!dragging.value) return;
  const dx = e.clientX - startX;
  // 左侧手柄向右拖 = 变宽；右侧手柄向左拖 = 变宽（取负）
  setPanelWidth(props.side, startWidth + (props.side === 'list' ? dx : -dx));
};

const onUp = (e: PointerEvent): void => {
  if (!dragging.value) return;
  dragging.value = false;
  capture(e.currentTarget as Element | null, e.pointerId, false);
  document.body.classList.remove('is-resizing');
  saveLayout();
};

const onReset = (): void => {
  resetPanelWidth(props.side);
  saveLayout();
};
</script>

<template>
  <div
    class="panel-resizer"
    :class="[`is-${side}`, { 'is-dragging': dragging }]"
    :data-side="side"
    role="separator"
    aria-orientation="vertical"
    :aria-label="side === 'list' ? '拖拽调整图层栏宽度（双击复位）' : '拖拽调整属性栏宽度（双击复位）'"
    @pointerdown="onDown"
    @pointermove="onMove"
    @pointerup="onUp"
    @pointercancel="onUp"
    @dblclick="onReset"
  />
</template>
